import { Coordinates, MapBounds, AppUser, TerrainPreference, TripLodging, RoutePlanAlternative, RouteScoreBreakdown, CampingSpot, RoadBarrier, RouteBarrierWarning } from '../types';
import { fetchDrivingRouteAlternatives, fetchMultiWaypointRoute } from './routingService';
import { fetchCandidateSpots } from './overpassService';
import { fetchWikiForPath } from './wikiService';
import { getCachedSpotsInArea } from './firebaseService';
import { fetchRoadBarriers, fetchOSMBarriers, fetchSwedishBarriers } from './nvdbService';
import { detectCountry } from '../utils/countryDetection';
import { getDistance } from './geometryUtils';

// --- Terrain-filtrering (gjenbruker App.tsx-logikken) ---

function spotMatchesTerrainPrefs(
  highway: string,
  terrainPrefs: TerrainPreference[] | undefined
): boolean {
  if (!terrainPrefs || terrainPrefs.length === 0) return true;
  const h = (highway || '').toLowerCase();
  const showTracks = terrainPrefs.includes('forest') || terrainPrefs.includes('mountain');
  const showGravel = terrainPrefs.includes('gravel') || showTracks;
  if (h === 'track' && !showTracks) return false;
  if (h === 'unclassified' && !showGravel) return false;
  return true;
}

// --- Hjelpefunksjoner ---

function routeBounds(coords: [number, number][]): MapBounds {
  let latMin = 90, latMax = -90, lonMin = 180, lonMax = -180;
  for (const [lon, lat] of coords) {
    if (lat < latMin) latMin = lat;
    if (lat > latMax) latMax = lat;
    if (lon < lonMin) lonMin = lon;
    if (lon > lonMax) lonMax = lon;
  }
  const latBuf = (latMax - latMin) * 0.05 + 0.01;
  const lonBuf = (lonMax - lonMin) * 0.05 + 0.01;
  return { latMin: latMin - latBuf, latMax: latMax + latBuf, lonMin: lonMin - lonBuf, lonMax: lonMax + lonBuf };
}

/** Sjekk om et punkt er innenfor maxMeters fra en polyline (OSRM [lon,lat] format) */
function isNearRoute(
  pointLat: number,
  pointLon: number,
  routeCoords: [number, number][],
  maxMeters: number
): boolean {
  const step = Math.max(1, Math.floor(routeCoords.length / 200));
  for (let i = 0; i < routeCoords.length; i += step) {
    const [lon, lat] = routeCoords[i];
    if (getDistance(pointLat, pointLon, lat, lon) <= maxMeters) return true;
  }
  return false;
}

/** Konverter OSRM [lon,lat][] til Coordinates[] for wiki-service */
function osrmToCoordPath(coords: [number, number][], sampleCount: number = 50): Coordinates[] {
  if (coords.length <= sampleCount) {
    return coords.map(([lon, lat]) => ({ latitude: lat, longitude: lon }));
  }
  const step = Math.max(1, Math.floor(coords.length / sampleCount));
  const result: Coordinates[] = [];
  for (let i = 0; i < coords.length; i += step) {
    const [lon, lat] = coords[i];
    result.push({ latitude: lat, longitude: lon });
  }
  const last = coords[coords.length - 1];
  result.push({ latitude: last[1], longitude: last[0] });
  return result;
}

// --- Scoring ---

function computeScore(
  matchingFricampSpots: number,
  attractionCount: number,
  terrainVariety: number,
  durationSeconds: number,
  fastestDuration: number
): { score: number; breakdown: RouteScoreBreakdown } {
  const fricampScore = Math.min(40, matchingFricampSpots * 4);
  const attractionScore = Math.min(25, attractionCount * 2.5);
  const scenicScore = Math.min(20, terrainVariety * 5);

  const timeFraction = fastestDuration > 0
    ? (durationSeconds - fastestDuration) / fastestDuration
    : 0;
  const timePenalty = Math.max(-15, Math.round(-timeFraction * 30));

  const total = Math.max(0, Math.min(100, Math.round(
    fricampScore + attractionScore + scenicScore + timePenalty
  )));

  return {
    score: total,
    breakdown: {
      fricampScore: Math.round(fricampScore),
      attractionScore: Math.round(attractionScore),
      scenicScore: Math.round(scenicScore),
      timePenalty,
    },
  };
}

// --- Barrier checking for routes ---

/** Check if a barrier is "locked" (i.e. not easily passable). */
function isLockedBarrier(barrier: RoadBarrier): boolean {
  const om = (barrier.openingMethod || '').toLowerCase();
  const type = (barrier.type || '').toLowerCase();
  // Payment barriers and open barriers are not "locked"
  if (om.includes('betaling') || type.includes('betalingsbom')) return false;
  if (om.includes('åpen') || om.includes('open')) return false;
  // Everything else: locked, manual, key, electric, unknown = locked
  return true;
}

/** Check a single route for barriers within proximity. Returns warnings sorted by distance along route. */
function checkRouteBarriers(
  routeCoords: [number, number][],
  barriers: RoadBarrier[],
  proximityMeters: number = 50
): RouteBarrierWarning[] {
  const warnings: RouteBarrierWarning[] = [];
  const step = Math.max(1, Math.floor(routeCoords.length / 500));

  for (const barrier of barriers) {
    if (!barrier.location || barrier.location.latitude == null || barrier.location.longitude == null) continue;
    let minDist = Infinity;
    let closestIdx = 0;
    for (let i = 0; i < routeCoords.length; i += step) {
      const [lon, lat] = routeCoords[i];
      const d = getDistance(barrier.location.latitude, barrier.location.longitude, lat, lon);
      if (d < minDist) {
        minDist = d;
        closestIdx = i;
      }
    }
    if (minDist <= proximityMeters) {
      // Estimate distance along route to this point
      let distAlong = 0;
      for (let i = 1; i <= Math.min(closestIdx, routeCoords.length - 1); i++) {
        const [lon1, lat1] = routeCoords[i - 1];
        const [lon2, lat2] = routeCoords[i];
        distAlong += getDistance(lat1, lon1, lat2, lon2);
      }
      warnings.push({
        barrierId: barrier.id,
        location: barrier.location,
        type: barrier.type || 'ukjent',
        openingMethod: barrier.openingMethod,
        isLocked: isLockedBarrier(barrier),
        distanceAlongRoute: distAlong,
      });
    }
  }
  warnings.sort((a, b) => a.distanceAlongRoute - b.distanceAlongRoute);
  return warnings;
}

/** Fetch barriers in the bounding area of routes and check each alternative. */
export async function checkRoutesForBarriers(
  alternatives: RoutePlanAlternative[],
  swedenEnabled?: boolean,
): Promise<RoutePlanAlternative[]> {
  if (alternatives.length === 0) return alternatives;

  // Compute combined bounding box for all routes
  const allCoords = alternatives.flatMap(a => a.coordinates);
  const bounds = routeBounds(allCoords);

  // Fetch barriers in the area (country-aware)
  let barriers: RoadBarrier[] = [];
  try {
    const centerLat = (bounds.latMin + bounds.latMax) / 2;
    const centerLon = (bounds.lonMin + bounds.lonMax) / 2;
    const country = detectCountry(centerLat, centerLon);

    const fetchers: Promise<RoadBarrier[]>[] = [];
    if (country === 'NO' || country === null) fetchers.push(fetchRoadBarriers(bounds));
    if ((country === 'SE' || country === null) && swedenEnabled) fetchers.push(fetchSwedishBarriers(bounds));
    fetchers.push(fetchOSMBarriers(bounds));

    const results = await Promise.all(fetchers);
    barriers = results.flat();
  } catch {
    // If barrier fetch fails, return routes without warnings
    return alternatives;
  }

  if (barriers.length === 0) return alternatives;

  return alternatives.map(alt => ({
    ...alt,
    barrierWarnings: checkRouteBarriers(alt.coordinates, barriers),
  }));
}

// Maks tillatt tid-overhead i fht raskeste rute (80% lengre = filtreres ut)
const MAX_DURATION_OVERHEAD = 1.8;

// ─── Steg 1: Hent rutealternativer (raskt, bare OSRM) ───

export async function fetchRouteAlternatives(
  from: Coordinates,
  to: Coordinates,
): Promise<RoutePlanAlternative[]> {
  const osrmAlternatives = await fetchDrivingRouteAlternatives(from, to, 3);
  if (osrmAlternatives.length === 0) throw new Error('no_routes');

  const fastestDuration = Math.min(...osrmAlternatives.map(a => a.durationSeconds));

  // Filtrer bort ruter som tar urimelig lang tid
  const reasonable = osrmAlternatives.filter(alt =>
    alt.durationSeconds <= fastestDuration * MAX_DURATION_OVERHEAD
  );

  return reasonable.map((alt, i) => ({
    index: i,
    coordinates: alt.coordinates,
    distanceMeters: alt.distanceMeters,
    durationSeconds: alt.durationSeconds,
    score: 0,
    scoreBreakdown: { fricampScore: 0, attractionScore: 0, scenicScore: 0, timePenalty: 0 },
    fricampSpotCount: 0,
    attractionCount: 0,
    label: i === 0 ? 'fastest' : 'alternative',
    poiIds: [],
    fullPolyline: alt.coordinates.map(([lon, lat]) => ({ latitude: lat, longitude: lon })),
  }));
}

// ─── Steg 2: Analyser innhold langs én valgt rute (on-demand) ───

export async function analyzeRouteContent(
  alt: RoutePlanAlternative,
  fastestDuration: number,
  user: AppUser,
  onStatus?: (msg: string) => void,
): Promise<RoutePlanAlternative> {
  const PROXIMITY_M = 1500;
  const terrainPrefs = user.activeVehicleTerrainPrefs;

  // Hent POI-data for denne rutens bounding box
  onStatus?.('Henter fricamp-plasser langs ruten...');
  const bounds = routeBounds(alt.coordinates);

  const [candidateSpots, cachedSpots] = await Promise.all([
    fetchCandidateSpots(bounds, { minDistanceHouses: 150, nearWater: false }, [])
      .catch(() => [] as CampingSpot[]),
    getCachedSpotsInArea(bounds)
      .catch(() => [] as CampingSpot[]),
  ]);

  // Dedupliser
  const allFricampSpots: CampingSpot[] = [...cachedSpots];
  for (const candidate of candidateSpots) {
    const isDup = cachedSpots.some(cached =>
      getDistance(cached.location.latitude, cached.location.longitude,
                 candidate.location.latitude, candidate.location.longitude) < 50
    );
    if (!isDup) allFricampSpots.push(candidate);
  }

  // Filtrer nær ruten
  const nearbyFricamp = allFricampSpots.filter(spot =>
    isNearRoute(spot.location.latitude, spot.location.longitude, alt.coordinates, PROXIMITY_M)
  );
  const matchingFricamp = nearbyFricamp.filter(spot =>
    spotMatchesTerrainPrefs(spot.highway, terrainPrefs)
  );

  // Attraksjoner
  onStatus?.('Henter attraksjoner...');
  let attractionCount = 0;
  try {
    const wiki = await fetchWikiForPath(osrmToCoordPath(alt.coordinates));
    attractionCount = wiki.length;
  } catch { /* ignorer wiki-feil */ }

  // Terrengvariasjon
  const uniqueHighways = new Set(nearbyFricamp.map(s => (s.highway || '').toLowerCase()).filter(Boolean));
  const terrainVariety = Math.min(4, uniqueHighways.size);

  const { score, breakdown } = computeScore(
    matchingFricamp.length,
    attractionCount,
    terrainVariety,
    alt.durationSeconds,
    fastestDuration
  );

  return {
    ...alt,
    score,
    scoreBreakdown: breakdown,
    fricampSpotCount: matchingFricamp.length,
    attractionCount,
    label: alt.label,
    poiIds: matchingFricamp.map(s => s.id),
    fullPolyline: alt.coordinates.map(([lon, lat]) => ({ latitude: lat, longitude: lon })),
  };
}

// ─── Korridor-kandidater (for reise-copiloten) ───
//
// Til forskjell fra analyzeRouteContent (som returnerer poiIds + tellinger for scoring)
// returnerer denne SPOT-OBJEKTER med koordinater, så copilotens selectDayStops kan
// projisere dem på ruten og velge dagsetapper. analyzeRouteContent røres ikke — dette
// er en egen kodesti (ingen regresjon på A→B-ruteplan/scoring).
//
// Segmentering: én Overpass-spørring over hele en Oslo→Nordkapp-bbox timer ut. Vi
// deler polylinjen i ~150 km-segmenter og spør per segment (parallelt, cappet).

const CORRIDOR_MAX_SEGMENT_KM = 150;
const CORRIDOR_MAX_SEGMENTS = 16;
const CORRIDOR_PROXIMITY_M = 1500;
// Maks tid per Overpass/Firestore-jobb før vi gir opp den og returnerer [] (ytelse).
// Firestore-cachen svarer typisk <1s; Overpass er den trege — den kappes her.
const CORRIDOR_JOB_TIMEOUT_MS = 5000;

/** Kumulativ avstand (meter) langs en OSRM [lon,lat]-polyline; samme lengde som coords. */
export function cumulativeDistances(coords: [number, number][]): number[] {
  const out: number[] = new Array(coords.length);
  let acc = 0;
  for (let i = 0; i < coords.length; i++) {
    if (i > 0) {
      const [lon1, lat1] = coords[i - 1];
      const [lon2, lat2] = coords[i];
      acc += getDistance(lat1, lon1, lat2, lon2);
    }
    out[i] = acc;
  }
  return out;
}

/** Del polylinjen i bounding-box-segmenter på ~maxSegmentKm, cappet til maxSegments. */
export function segmentRouteBounds(
  coords: [number, number][],
  maxSegmentKm: number = CORRIDOR_MAX_SEGMENT_KM,
  maxSegments: number = CORRIDOR_MAX_SEGMENTS,
): MapBounds[] {
  if (coords.length === 0) return [];
  const cum = cumulativeDistances(coords);
  const totalKm = cum[cum.length - 1] / 1000;
  // Øk segmentstørrelsen hvis vi ellers ville sprengt maxSegments.
  const segKm = Math.max(maxSegmentKm, totalKm / maxSegments);
  const segMeters = segKm * 1000;

  const segments: MapBounds[] = [];
  let segStart = 0;
  let nextCut = segMeters;
  for (let i = 0; i < coords.length; i++) {
    if (cum[i] >= nextCut || i === coords.length - 1) {
      const slice = coords.slice(segStart, i + 1);
      if (slice.length >= 1) segments.push(routeBounds(slice));
      segStart = i;
      nextCut += segMeters;
    }
  }
  return segments;
}

/** Dedupliser spots på id og på nær-koordinat (<50 m). */
function dedupeSpots(spots: CampingSpot[]): CampingSpot[] {
  const out: CampingSpot[] = [];
  const seenIds = new Set<string>();
  for (const s of spots) {
    if (!s || !s.location) continue;
    if (s.id && seenIds.has(s.id)) continue;
    const dup = out.some(o =>
      getDistance(o.location.latitude, o.location.longitude, s.location.latitude, s.location.longitude) < 50
    );
    if (dup) continue;
    if (s.id) seenIds.add(s.id);
    out.push(s);
  }
  return out;
}

/**
 * Er plassen en OVERNATTINGS-kandidat? Severdigheter/fasiliteter (osm_amenity =
 * badeplasser m.m., attraction, promoterte rute-severdigheter) ligger i cached_spots
 * med isVerified og ble ellers plukket som «bekreftede» overnattingsstopp — en
 * severdighet brukeren trykket på kunne dukke opp som dag-stopp i neste plan. Ren.
 */
export function isOvernightCandidateType(spot: CampingSpot): boolean {
  if (spot.spotType === 'osm_amenity' || spot.spotType === 'attraction') return false;
  if (typeof spot.importVersion === 'string' && spot.importVersion.startsWith('route-attraction')) return false;
  return true;
}

/**
 * Hent overnattings-kandidater langs en korridor, med koordinater.
 * Kilde: Firestore `cached_spots` (både fricamp og seedede campingplasser) + live
 * Overpass-diskovering av NYE fricamp-noder. Campingplasser kommer KUN fra DB (seedes
 * NO+SE+DK server-side) — ingen live-Overpass for camp. lodging avgjør hva som beholdes:
 * 'fricamp'/'camp'/'mix'. Fricamp-kandidater terreng-filtreres; campingplasser gjør ikke det.
 */
export async function fetchCorridorCandidates(
  coords: [number, number][],
  opts: {
    lodging: TripLodging;
    terrainPrefs?: TerrainPreference[];
    proximityMeters?: number;
    onStatus?: (msg: string) => void;
    /** Brukerens favoritt-plasser — tas med i kandidatpoolen (nær-rute-filtrert). */
    extraCandidates?: CampingSpot[];
  },
): Promise<CampingSpot[]> {
  if (coords.length === 0) return [];
  const proximity = opts.proximityMeters ?? CORRIDOR_PROXIMITY_M;
  const wantFricamp = opts.lodging === 'fricamp' || opts.lodging === 'mix';
  const wantCamp = opts.lodging === 'camp' || opts.lodging === 'mix';

  const segments = segmentRouteBounds(coords);
  opts.onStatus?.('Leter etter overnattingsplasser langs ruten...');

  // Per-jobb-timeout: de offentlige Overpass-serverne 429-er/henger (6s-retries × 3
  // endepunkter). Hver jobb gir opp etter CORRIDOR_JOB_TIMEOUT_MS og returnerer [], så
  // raske Firestore-treff bevares selv når Overpass er nede. Holder totaltiden nede.
  const withTimeout = (p: Promise<CampingSpot[]>): Promise<CampingSpot[]> => {
    let timer: ReturnType<typeof setTimeout>;
    const timeout = new Promise<CampingSpot[]>((resolve) => { timer = setTimeout(() => resolve([]), CORRIDOR_JOB_TIMEOUT_MS); });
    return Promise.race([p.catch(() => [] as CampingSpot[]), timeout]).finally(() => clearTimeout(timer));
  };

  const perSegment = await Promise.all(segments.map(async (bounds) => {
    const jobs: Promise<CampingSpot[]>[] = [];
    // Firestore-cachen (`cached_spots`) er kilden for campingplasser — den seedes NO+SE+DK
    // server-side (seedOsmCampsitesScheduled → campsiteDocFromElement, spotType=
    // 'commercial_campsite') og inneholder også fricamp. Ingen live-Overpass for camp
    // lenger: det ga 429/tynne ruter, og DB-en er nå den etablerte kilden (samme vei som
    // kartlagene via getSpotMarkers). Kjøres ALLTID, svarer <1s.
    jobs.push(withTimeout(getCachedSpotsInArea(bounds)));
    // Fricamp-DISKOVERING fra OSM (nye villmarksplasser ikke i DB) er fortsatt et live
    // Overpass-oppslag, men timeout-vaktet og supplert av DB-treff over.
    if (wantFricamp) jobs.push(withTimeout(fetchCandidateSpots(bounds, { minDistanceHouses: 150, nearWater: false }, [])));
    const res = await Promise.all(jobs);
    return res.flat();
  }));

  const extra = Array.isArray(opts.extraCandidates) ? opts.extraCandidates : [];
  const all = dedupeSpots([...extra, ...perSegment.flat()]);

  // Nær ruten + filtrer på ønsket overnattingstype (lodging). Campingplasser er
  // vei-tilgjengelige (ingen terreng-filter); fricamp-typer terreng-filtreres.
  return all.filter((spot) => {
    if (!isOvernightCandidateType(spot)) return false;
    if (!isNearRoute(spot.location.latitude, spot.location.longitude, coords, proximity)) return false;
    if (spot.spotType === 'commercial_campsite') return wantCamp;
    if (!wantFricamp) return false;
    return spotMatchesTerrainPrefs(spot.highway, opts.terrainPrefs);
  });
}

// ─── Multi-stopp rute (A → S1 → S2 → B) ───

export async function fetchMultiStopRoute(
  from: Coordinates | [number, number],
  stops: Coordinates[],
  to: Coordinates | [number, number],
): Promise<RoutePlanAlternative[]> {
  // Normalize coordinates if they are passed as arrays [lon, lat]
  const normFrom = Array.isArray(from) ? { longitude: from[0], latitude: from[1] } : from;
  const normTo = Array.isArray(to) ? { longitude: to[0], latitude: to[1] } : to;

  const waypoints = [normFrom, ...stops, normTo];
  const result = await fetchMultiWaypointRoute(waypoints);

  return [{
    index: 0,
    coordinates: result.coordinates,
    distanceMeters: result.distanceMeters,
    durationSeconds: result.durationSeconds,
    score: 0,
    scoreBreakdown: { fricampScore: 0, attractionScore: 0, scenicScore: 0, timePenalty: 0 },
    fricampSpotCount: 0,
    attractionCount: 0,
    label: 'route',
    poiIds: [],
    fullPolyline: result.coordinates.map(([lon, lat]) => ({ latitude: lat, longitude: lon })),
  }];
}
